"use client";

import { Bolt, Loader2, UsersRound } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { localizedText } from "@/i18n/text";
import type { ApplicationMode } from "@/types/listing";

type Props = {
  mode?: ApplicationMode | null;
  companyName?: string | null;
  /** Null while we do not yet know, which is not the same as "not in the queue". */
  isInQueue: boolean | null;
  /** False when the company runs no queue, so there is nothing to join. */
  canJoinQueue: boolean;
  isJoining?: boolean;
  onJoinQueue?: () => void;
};

/**
 * Tells the student, before they click, what applying for this home takes.
 *
 * The queue requirement used to surface only as a refused application. That is
 * the wrong moment for it: the student has already decided they want the home,
 * and the fix — join the queue — is a different page away. Here the requirement
 * and the way to satisfy it sit in the same block, above the apply button.
 */
export default function ApplicationModeNotice({
  mode,
  companyName,
  isInQueue,
  canJoinQueue,
  isJoining = false,
  onJoinQueue,
}: Props) {
  const { locale } = useI18n();

  if (mode !== "QUEUE_REQUIRED" && mode !== "FIRST_COME") {
    return null;
  }

  const company =
    companyName?.trim() || localizedText(locale, "företaget", "the company");

  if (mode === "FIRST_COME") {
    return (
      <section className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-400 text-gray-950 shadow-sm ring-1 ring-black/5">
          <Bolt className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 text-sm text-amber-950">
          <p className="font-bold uppercase tracking-[0.09em] text-xs">
            Express
          </p>
          <p className="mt-1 leading-6">
            {localizedText(
              locale,
              `Ingen bostadskö krävs. Ansök direkt — ${company} hanterar ansökningarna i den ordning de kommer in.`,
              `No housing queue required. Apply directly — ${company} handles applications in the order they arrive.`
            )}
          </p>
        </div>
      </section>
    );
  }

  // Queue required. Three states, because "we don't know yet" must not be shown
  // as "you are not in the queue" — that would push the student to join a queue
  // they may already be standing in.
  if (isInQueue === null) {
    return (
      <section className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
        <UsersRound className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden />
        <div className="min-w-0 text-sm text-gray-700">
          <p className="font-semibold">
            {localizedText(locale, "Kräver bostadskö", "Requires housing queue")}
          </p>
          <p className="mt-0.5 leading-6">
            {localizedText(
              locale,
              `Den här bostaden kräver att du står i ${company}s bostadskö.`,
              `This home requires you to be in ${company}'s housing queue.`
            )}
          </p>
        </div>
      </section>
    );
  }

  if (isInQueue) {
    return (
      <section className="flex items-start gap-3 rounded-2xl border border-brand-100 bg-brand-50 px-4 py-3">
        <UsersRound className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" aria-hidden />
        <div className="min-w-0 text-sm text-brand-700">
          <p className="font-semibold">
            {localizedText(locale, "Du står i kön", "You are in the queue")}
          </p>
          <p className="mt-0.5 leading-6">
            {localizedText(
              locale,
              `Den här bostaden kräver ${company}s bostadskö, och där står du redan. Du kan söka direkt.`,
              `This home requires ${company}'s housing queue, and you are already in it. You can apply right away.`
            )}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-start gap-3">
        <UsersRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
        <div className="min-w-0 text-sm text-amber-900">
          <p className="font-semibold">
            {localizedText(
              locale,
              "Du står inte i kön ännu",
              "You are not in the queue yet"
            )}
          </p>
          <p className="mt-0.5 leading-6">
            {canJoinQueue
              ? localizedText(
                  locale,
                  `Den här bostaden kräver ${company}s bostadskö. Ställ dig i kön först, så kan du söka.`,
                  `This home requires ${company}'s housing queue. Join the queue first, then you can apply.`
                )
              : localizedText(
                  locale,
                  `Den här bostaden kräver ${company}s bostadskö, men kön tar inte emot nya just nu.`,
                  `This home requires ${company}'s housing queue, but the queue is not accepting new members right now.`
                )}
          </p>

          {canJoinQueue && onJoinQueue && (
            <Button
              className="mt-3 rounded-full"
              isDisabled={isJoining}
              onClick={onJoinQueue}
              size="sm"
              type="button"
            >
              {isJoining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UsersRound className="h-4 w-4" />
              )}
              {localizedText(locale, "Ställ dig i kön", "Join the queue")}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
